import React from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { contactFormSchema } from "../../yup";

interface ContactFormValues {
  name: string;
  email: string;
}

interface Props {
  messageId: string;
  question: string;
  submitted: boolean;
  onSubmit: (
    messageId: string,
    name: string,
    email: string,
    question: string
  ) => Promise<boolean>;
}

const baseInputCls =
  "w-full bg-white rounded-lg py-2 px-3 text-[11px] font-medium border outline-none placeholder:text-gray-400 transition-all";
const okInputCls =
  "border-gray-200 focus:ring-1 focus:ring-[#090F21] focus:border-[#090F21]";
const errInputCls =
  "border-red-300 focus:ring-1 focus:ring-red-400 focus:border-red-400";

const ContactForm: React.FC<Props> = ({
  messageId,
  question,
  submitted,
  onSubmit,
}) => {
  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<ContactFormValues>({
    resolver: yupResolver(contactFormSchema),
    mode: "onBlur",
    defaultValues: { name: "", email: "" },
  });

  if (submitted) {
    return (
      <div className="mt-2 max-w-[85%] w-full bg-green-50 border border-green-200 rounded-xl px-3.5 py-3">
        <p className="text-xs text-green-700 font-medium">
          ✓ Request sent. The Inception team will reach out to you shortly.
        </p>
      </div>
    );
  }

  const submitHandler = handleSubmit(async ({ name, email }) => {
    clearErrors("root.serverError");
    const ok = await onSubmit(messageId, name.trim(), email.trim(), question);
    if (!ok) {
      setError("root.serverError", {
        type: "server",
        message: "Failed to submit. Please try again.",
      });
    }
  });

  return (
    <form
      noValidate
      onSubmit={submitHandler}
      className="mt-2 max-w-[85%] w-full bg-[#090F21]/5 border border-[#090F21]/20 rounded-xl px-3.5 py-3"
    >
      <p className="text-xs font-semibold text-[#090F21] mb-2.5">
        Connect with the Inception team
      </p>
      <div className="space-y-2">
        <div>
          <input
            {...register("name")}
            type="text"
            placeholder="Your name"
            autoComplete="name"
            maxLength={100}
            aria-invalid={!!errors.name}
            aria-describedby={errors.name ? `${messageId}-name-error` : undefined}
            className={`${baseInputCls} ${errors.name ? errInputCls : okInputCls}`}
          />
          {errors.name && (
            <p
              id={`${messageId}-name-error`}
              className="text-[10px] text-red-500 mt-1 ml-0.5"
            >
              {errors.name.message}
            </p>
          )}
        </div>

        <div>
          <input
            {...register("email")}
            type="email"
            placeholder="Your email address"
            autoComplete="email"
            inputMode="email"
            maxLength={254}
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? `${messageId}-email-error` : undefined}
            className={`${baseInputCls} ${errors.email ? errInputCls : okInputCls}`}
          />
          {errors.email && (
            <p
              id={`${messageId}-email-error`}
              className="text-[10px] text-red-500 mt-1 ml-0.5"
            >
              {errors.email.message}
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="!bg-[#090F21] text-white text-[11px] font-medium px-4 py-2 rounded-lg hover:!bg-[#1a2540] active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? "Sending..." : "Send Request"}
        </button>

        {errors.root?.serverError?.message && (
          <p className="text-[10px] text-red-500" role="alert">
            {errors.root.serverError.message}
          </p>
        )}
      </div>
    </form>
  );
};

export default ContactForm;
